import { computed, readonly, ref } from "vue";

export type EntityDetailLoadPhase =
	| "idle"
	| "loading"
	| "ready"
	| "not-found"
	| "signed-out"
	| "error"
	| "project-refresh-error"
	| "timeline-error"
	| "account-error"
	| "auth-error"
	| "trial-error";
export type EntityDetailRecoveryStage = "auth" | "account" | "trial" | "project" | "timeline";
type RecoveryActions = {
	initialize: (token: number) => Promise<void>;
	projectVerdict: (token: number) => Promise<void>;
	projectStage: (token: number, stage: EntityDetailRecoveryStage) => Promise<void>;
};
export type EntityDetailStopReason =
	| "entity-missing"
	| "not-found"
	| "auth-lost"
	| "signed-out"
	| "access-denied"
	| "superseded"
	| "unmounted"
	| `stage-failed:${EntityDetailRecoveryStage | "verdict" | "content"}`;
const stopPhases: Record<EntityDetailStopReason, TerminalPhase> = {
	"entity-missing": "project-refresh-error",
	"not-found": "not-found",
	"auth-lost": "auth-error",
	"signed-out": "signed-out",
	"access-denied": "ready",
	superseded: "error",
	unmounted: "error",
	"stage-failed:auth": "auth-error",
	"stage-failed:account": "account-error",
	"stage-failed:trial": "trial-error",
	"stage-failed:project": "project-refresh-error",
	"stage-failed:timeline": "timeline-error",
	"stage-failed:verdict": "error",
	"stage-failed:content": "error"
};
type TerminalPhase = Exclude<EntityDetailLoadPhase, "idle" | "loading">;

/** Single spinner owner. A new attempt or cancellation revokes the old token synchronously. */
export function useEntityDetailLoad(sessionLost: () => boolean = () => false) {
	const phase = ref<EntityDetailLoadPhase>("idle");
	const loading = computed(() => phase.value === "loading");
	let sequence = 0;
	const completed = new Set<EntityDetailRecoveryStage>();
	function resetStages(): void {
		completed.clear();
	}
	function complete(stage: EntityDetailRecoveryStage): void {
		completed.add(stage);
	}
	function hasCompleted(stage: EntityDetailRecoveryStage): boolean {
		return completed.has(stage);
	}
	function invalidate(stage: EntityDetailRecoveryStage): void {
		completed.delete(stage);
	}
	const recoveryStage = computed<EntityDetailRecoveryStage | null>(() =>
		phase.value === "auth-error"
			? "auth"
			: phase.value === "trial-error"
				? "trial"
				: phase.value === "account-error"
					? "account"
					: phase.value === "project-refresh-error"
						? "project"
						: phase.value === "timeline-error"
							? "timeline"
							: null
	);
	function begin(): number {
		const token = ++sequence;
		phase.value = "loading";
		if (sessionLost()) settle(token, "auth-lost", "auth-error");
		return token;
	}
	/** Claims ownership for a refresh of an already-ready page without showing the page spinner. */
	function beginBackground(): number {
		return ++sequence;
	}
	/** Settles a [beginBackground] refresh: a terminal outcome replaces the ready page, success leaves it. */
	function settleBackground(token: number, reason: EntityDetailStopReason | null, success: TerminalPhase): void {
		if (!owns(token) || phase.value !== "ready") return;
		// A discarded overlapping refresh is not a failure of the page that is already on screen.
		if (reason === "superseded" || reason === "unmounted") return;
		phase.value = reason ? stopPhases[reason] : success;
	}
	function finish(token: number, terminal: TerminalPhase): void {
		if (token === sequence && phase.value === "loading") phase.value = terminal;
	}
	/** Stop causes take precedence over content-derived fallbacks. Stale owners never settle a successor. */
	function settle(token: number, reason: EntityDetailStopReason | null, success: TerminalPhase): void {
		if (!owns(token)) return;
		const terminal = reason ? stopPhases[reason] : success;
		if (reason && phase.value !== "loading" && phase.value !== terminal)
			throw new Error(`Entity detail settlement does not match stop reason ${reason}: ${phase.value}`);
		finish(token, terminal);
	}

	function supersede(terminal: TerminalPhase = "error"): void {
		++sequence;
		phase.value = sessionLost() ? stopPhases["auth-lost"] : terminal;
	}
	function owns(token: number): boolean {
		return token === sequence;
	}
	async function retry(entityType: string | null, actions: RecoveryActions): Promise<void> {
		// A queued second tap must not revoke the operation already recovering this page.
		if (sessionLost() || phase.value === "loading") return;
		const stage = recoveryStage.value;
		const recover = stage
			? (token: number) => actions.projectStage(token, stage)
			: entityType !== "project"
				? actions.initialize
				: actions.projectVerdict;
		const token = begin();
		try {
			await recover(token);
		} finally {
			// An early exit still offers recovery; a stale completion cannot settle its successor.
			if (loading.value) settle(token, `stage-failed:${stage ?? "content"}`, "error");
		}
	}
	return {
		currentToken: (): number => sequence,
		resetStages,
		complete,
		hasCompleted,
		invalidate,
		recoveryStage,
		retry,
		phase: readonly(phase),
		loading,
		begin,
		beginBackground,
		settleBackground,
		finish,
		settle,
		supersede,
		owns
	};
}
